/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { POSCatalogModelData } from '../../../../../common/libraries/modelViewer/Data/POSCatalogData';
import { useAppSelector } from '../../../../../Redux/Store/hooks';
import { FilePaths } from '../../../../../common/libraries/fileSystem/filePaths';
import { CartItem, CartItemHelper } from '../../../../Data/CartItem';

import { ModelGroupInfo } from '../../../../../common/libraries/fileFormats/sltx/modelGroupInfo';
import { Currencies } from '../../../../Util/currencies';
import { CatalogFileCustomAttributesDefinition, PriceData } from '../../../../../common/libraries/fileFormats/catalog/catalog';
import { CatalogTagsTranslation } from '../../../../../common/libraries/modelViewer/Data/catalogTagsTranslation';
import { CustomAttributeData } from '../../../../../common/libraries/customAttributes/customAttributes';
import { SLTXFile } from '../../../../../common/libraries/fileFormats/sltx/sltxFile';
import { SLTXFormatRootFileGroupOption } from '../../../../../common/libraries/fileFormats/sltx/sltx';
import { SLTXTranslations } from '../../../../../common/libraries/fileFormats/sltx/sltxTranslations';

// Define the props interface for the component
interface CartItemThumbnailProps {
  catalogObject: POSCatalogModelData;
  cartItem: CartItem;
  cartItemIndex: number;
  customOrderLineAttributesData : CatalogFileCustomAttributesDefinition | undefined;
  priceData: PriceData[] | undefined,
  tagsTranslation: CatalogTagsTranslation[] | undefined;
}

export function CartItemDetails({
  catalogObject,
  cartItem,
  cartItemIndex,
  customOrderLineAttributesData,
  priceData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tagsTranslation,
} : CartItemThumbnailProps) : JSX.Element {

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sltxFile, setSLTXFile] = useState<SLTXFile | undefined>();
  const [globalShowPrices, setGlobalShowPrices] = useState<boolean>(false);

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    const showPricesValue = computedStyle.getPropertyValue('--show-prices').trim();
    setGlobalShowPrices(showPricesValue === 'true');
  }, []);

  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const translations = useAppSelector((state) => {
    return state.TextsReducer.translation;
  })!;

  const selectedLanguage = useAppSelector((state) => {
    return state.TextsReducer.language;
  })!;

  // From the store, get the environment settings
  const environmentSettings = useAppSelector((state) => {
    return state.EnvironmentSettingsReducer;
  });

  if (cartItemIndex < 0) {
    return <div className="empty" />;
  }

  // Check we this is an predefined variant
  if (catalogObject === undefined
    || catalogObject.data === undefined
    || catalogObject.data.variants === undefined
    || catalogObject.catalogData === undefined
  ) {
    return <div className="empty" />;
  }

  const catalogObjectGroups = catalogObject.data.groups;
  if (catalogObjectGroups === undefined) {
    return <div className="empty" />;
  }

  if (cartItem === undefined) {
    return <div className="empty" />;
  }

  const itemCustomVariant = CartItemHelper.GetVariant(cartItem);

  cartItem.objectCustomAttributes.sort((a, b) => {
    const aAttrDef = catalogObject.data?.customAttributes?.find((e) => e.key === a.attributeKey);
    const bAttrDef = catalogObject.data?.customAttributes?.find((e) => e.key === b.attributeKey);
    if (aAttrDef !== undefined && bAttrDef !== undefined && (aAttrDef.priority - bAttrDef.priority) !== 0) {
      return aAttrDef.priority - bAttrDef.priority;
    }
    return a.attributeName.localeCompare(b.attributeName);
  });
  cartItem.orderLineCustomAttributes.sort((a, b) => {
    const aAttrDef = customOrderLineAttributesData?.attributes.find((e) => e.key === a.attributeKey);
    const bAttrDef = customOrderLineAttributesData?.attributes.find((e) => e.key === b.attributeKey);
    if (aAttrDef !== undefined && bAttrDef !== undefined && (aAttrDef.priority - bAttrDef.priority) !== 0) {
      return aAttrDef.priority - bAttrDef.priority;
    }
    return a.attributeName.localeCompare(b.attributeName);
  });

  useEffect(() => {
    const modelFilePath = FilePaths.join(
      environmentSettings.catalogInfoPath,
      catalogObject.getFileInfoPath(),
    );

    const newSLTXFile = new SLTXFile();
    newSLTXFile.setFilePath(modelFilePath);
    newSLTXFile.openFile(catalogObject.getName()).then((result) => {
      if (result === false) {
        // eslint-disable-next-line no-console
        console.error(`Failed to load the info.json expected to be in ${modelFilePath}`);
      }
      setSLTXFile(newSLTXFile);
    });

  }, []);

  const getTranslatedComponentName = (componentId: number) => {
    if (sltxFile) return SLTXTranslations.getTranslatedComponentName(componentId, sltxFile, selectedLanguage);
    return '<undefined-3>';
  };

  const getSelectedOptionForComponent = (componentId: number, optionId: number, translate: boolean) => {
    if (sltxFile) return SLTXTranslations.getSelectedOptionTranslation(componentId, optionId, sltxFile, translate, selectedLanguage);
    return '<undefined-2>'; // objectComponentOption ? objectComponentOption.name : '';
  };

  return (
    <>
      {
        // Transverse the object groups, and for each group, grab the option
        // which the variant is using
        catalogObjectGroups.map((catalogObjectGroup, groupIndex) => {
          const groupID = catalogObjectGroup.id;
          const groupName = catalogObjectGroup.name;
          const priceInfo = cartItem.optionsPrice.find((p) => p.group === catalogObjectGroup.name);

          // Get the option selected by the variant for this group
          const itemVariantOption = itemCustomVariant.find(
            (variantOption) => variantOption.groupID === groupID,
          );

          if (itemVariantOption === undefined) {
            return <div className="empty" />;
          }

          const cartItemOptionID = itemVariantOption.optionID;

          // Get the information about this option from the catalogObject

          const groupOptions = ModelGroupInfo.getFolderWithOptions(catalogObjectGroup);

          let catalogItemGroupOption : any = null;

          groupOptions.forEach((value: SLTXFormatRootFileGroupOption[]) => {
            value.forEach((catalogObjectGroupOption) => {
              if (catalogObjectGroupOption.id === cartItemOptionID) {
                catalogItemGroupOption = catalogObjectGroupOption;
              }
            });
          });

          if (catalogItemGroupOption === undefined) {
            return <div className="empty" />;
          }

          const groupNameTranslated = getTranslatedComponentName(groupID) || groupName;
          const folderAndOptionTranslated = getSelectedOptionForComponent(groupID, cartItemOptionID, true);
          const optionThumbnailImage = catalogItemGroupOption.thumbnailRef;

          const thumbnailPath = FilePaths.join(
            environmentSettings.catalogInfoPath,
            FilePaths.join(
              catalogObject.getResourcesPath(),
              optionThumbnailImage,
            ),
          );

          let priceCurrency = catalogObject?.catalogData?.priceCurrency;
          if (!priceCurrency) {
            priceCurrency = 0;
          }

          if (!SLTXTranslations.IsGroupNameVisible(groupNameTranslated)) {
            // eslint-disable-next-line react/no-array-index-key
            return <div className="empty" key={`a-${cartItemIndex}.${groupIndex}`} />;
          }

          return (
            <div
              className="container"
              style={{
                display: 'flex',
                alignItems: 'left',
                paddingTop: '10px',
                paddingBottom: '4px',
                borderBottom: '1px solid #EEEEEE',
              }}
              // eslint-disable-next-line react/no-array-index-key
              key={`b-${cartItemIndex}-${groupIndex}`}
            >
              <div className="row justify-content-end">
                <div key="x" className="col">
                  <p className="orderGroupName">{`${groupNameTranslated}`}</p>
                </div>
                <div key="y" className="col">
                  <div style={{ display: 'inline-block' }}>
                    <div className="orderMaterialImageHolder">
                      <img
                        alt=""
                        className="orderMaterialImage"
                        src={thumbnailPath}
                      />
                    </div>
                  </div>
                </div>
                <div key="z" className="col">
                  <p className="orderOptionName">{`${folderAndOptionTranslated}`}</p>
                </div>
                { globalShowPrices && priceInfo !== undefined && priceInfo.price > 0 ? (
                  <div key="zz" style={{ float: 'right' }} className="col">
                    <p className="orderOptionName">{`${Currencies.formatPrice(priceInfo ? priceInfo.price : 0, priceCurrency)}`}</p>
                  </div>
                ) : (
                  <div key="zz" />
                )}
              </div>
            </div>
          );
        })
      }

      { /* To add specific measures from the model, etc */ }
      { cartItem.objectDetails.length > 0 && (
        <div key={`d-${cartItemIndex}}`}>
          <div
            key="a"
            className="container"
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '4px',
              paddingBottom: '4px',
            }}
          >
            <div className="row justify-content-end">
              <div className="col">
                <p className="orderDetailsTag">{strings.ORDER_DETAIL_TITLE_LABEL}</p>
              </div>
            </div>
          </div>

          <div
            key="b"
            className="container"
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '4px',
              paddingBottom: '4px',
            }}
          >
            {cartItem.objectDetails.map((detail) => {
              return (
                <div key={detail.measureType} className="row">
                  <div className="col-6">
                    <p className="orderOptionName"><b>{detail.measureType}</b></p>
                  </div>
                  <div className="col-6">
                    <p className="orderOptionName">{detail.measureValue}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {cartItem.objectCustomAttributes.map((attribute) => {
        const attrDef = catalogObject.data?.customAttributes?.find((e) => e.key === attribute.attributeKey);
        if (attrDef === undefined || !attribute.attributeEnabled) {
          return (
            <div key={`x-${attribute.attributeKey}`} className="empty" />
          );
        }
        if (attrDef.type === 'Separator') {
          return (
            <div
              key={`a-${attrDef.key}`}
              className="row"
              style={
                {
                  borderTop: '2px solid #444444',
                  borderBottom: '1px solid #444444',
                }
              }
            >
              <div className="col-6">
                <p className="orderOptionName" style={{ fontWeight: 'bold' }}>{attribute.attributeName}</p>
              </div>
            </div>
          );
        }

        let priceCurrency = catalogObject?.catalogData?.priceCurrency;
        if (!priceCurrency) {
          priceCurrency = 0;
        }
        const attributePrice = CustomAttributeData.getAttributePrice(attribute, priceData);
        const effectiveQuant = attribute.attributeQuantity < 0 && attributePrice > 0 ? 1 : attribute.attributeQuantity;
        const effectiveTotalPrice = effectiveQuant * attributePrice;
        const totalPrice = `${Currencies.formatPrice(effectiveTotalPrice, priceCurrency)}`;
        let priceString = effectiveQuant > 1 ? `${effectiveQuant} x ` : '';
        priceString += attributePrice > 0 && effectiveQuant > 1 ? `${attributePrice}` : '';
        priceString += effectiveTotalPrice > 0 ? ` = ${totalPrice}` : '';

        if (attrDef.type === 'Boolean' && attribute.attributeValue === 'false') { // but don't show anything because we're disabled..
          priceString = '';
        }

        let attrValue = attribute.attributeTranslatedValue ? attribute.attributeTranslatedValue : attribute.attributeValue;
        if (attrDef.type === 'Boolean' || attribute.attributeValue === 'false') {
          attrValue = attribute.attributeValue === 'true' ? strings.BOOLEAN_ATTRIBUTE_YES : strings.BOOLEAN_ATTRIBUTE_NO;
        }

        return (
          <div
            key={`aa-${attrDef.key}`}
            className="row"
            style={
              {
                borderBottom: '1px solid #EEEEEE',
              }
            }
          >
            <div key="a" className="col-5">
              <p className="orderOptionName">{`- ${attribute.attributeName}`}</p>
            </div>
            <div key="b" className="col-5">
              <p className="orderOptionName">{attrValue}</p>
            </div>
            <div key="c" className="col-2">
              { globalShowPrices
                ? (
                  <p className="orderOptionName">{priceString}</p>
                ) : (
                  <p className="orderOptionName" />
                )}
            </div>
          </div>
        );
      })}

      {cartItem.orderLineCustomAttributes.map((attribute) => {
        const attrDef = customOrderLineAttributesData?.attributes.find((e) => e.key === attribute.attributeKey);
        if (attrDef === undefined || !attribute.attributeEnabled) {
          return (
            <div key={`xx-${attribute.attributeKey}`} className="empty" />
          );
        }
        if (attrDef.type === 'Separator') {
          return (
            <div
              key={`xy-${attribute.attributeKey}`}
              className="row"
              style={
                {
                  borderTop: '2px solid #444444',
                  borderBottom: '1px solid #444444',
                }
              }
            >
              <div className="col-6">
                <p className="orderOptionName" style={{ fontWeight: 'bold' }}>{attribute.attributeName}</p>
              </div>
            </div>
          );
        }

        let priceCurrency = catalogObject?.catalogData?.priceCurrency;
        if (!priceCurrency) {
          priceCurrency = 0;
        }
        const attributePrice = CustomAttributeData.getAttributePrice(attribute, priceData);
        const effectiveQuant = attribute.attributeQuantity < 0 && attributePrice > 0 ? 1 : attribute.attributeQuantity;
        const effectiveTotalPrice = effectiveQuant * attributePrice;
        const totalPrice = `${Currencies.formatPrice(effectiveTotalPrice, priceCurrency)}`;
        let priceString = effectiveQuant > 1 ? `${effectiveQuant} x ` : '';
        priceString += attributePrice > 0 && effectiveQuant > 1 ? `${attributePrice} = ` : '';
        priceString += effectiveTotalPrice > 0 ? `${totalPrice}` : '';
        if (attrDef.type === 'Boolean' && attribute.attributeValue === 'false') { // but don't show anything because we're disabled..
          priceString = '';
        }

        let attrValue = attribute.attributeTranslatedValue ? attribute.attributeTranslatedValue : attribute.attributeValue;
        if (attrDef.type === 'Boolean' || attribute.attributeValue === 'false') {
          attrValue = attribute.attributeValue === 'true' ? strings.BOOLEAN_ATTRIBUTE_YES : strings.BOOLEAN_ATTRIBUTE_NO;
        }

        return (
          <div
            key={`xz-${attribute.attributeKey}`}
            className="row"
            style={
              {
                borderBottom: '1px solid #EEEEEE',
              }
            }
          >
            <div key="a" className="col-5">
              <p className="orderOptionName">{`- ${attribute.attributeName}`}</p>
            </div>
            <div key="b" className="col-5">
              <p className="orderOptionName">{attrValue}</p>
            </div>
            <div key="c" className="col-2">
              { globalShowPrices
                ? (
                  <p className="orderOptionName">{priceString}</p>
                ) : (
                  <p className="orderOptionName" />
                )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default CartItemDetails;
